/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Snigdha Dhanireddy <snigdha.dhanireddy@mroads.com>, June 2023
 * @format
 * @flow
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Snackbar, Button } from '@material-ui/core';
import useStyles from './useStyles';
import AssignTable from '../../AssignTable';
import TableProperties from '../../TableProperties';
import ConfirmationPopup from '../../ConfirmationPopup';
import { toast } from 'react-toastify';
import Whiteboard from '../../../../Playground/components/Body/Whiteboard';
import FireboardUtils from '../../../../../util/fireboard-util';

const defaultTableActionState = {
  clearTable: false,
  mergeTable: false,
  unmergeTable: false,
  transferTable: false,
  voidCheck: false,
  sms: false,
  assignTable: false,
};

const selectedTableColor = '#ffdab9';

const Body = props => {
  const classes = useStyles();
  const { labelsData } = useSelector(state => state);
  const { labels: strings } = labelsData;
  const {
    popup,
    goToWaitlistScreen,
    selectedBooking,
    assignTableMode,
    showTableProperties,
    onAssignTable,
    employees,
    selectedItem,
    selectedShape,
    selectedTableChecks,
    unseatGuest,
    shapes: shapeProps,
    onMergeTable,
    invokeTableUnmerge,
    onClickSms,
    onVoidCheck,
    onSelectShape,
    initiateMoveTable,
    onMoveTable,
    moveTableInfo,
    clearMoveTableInfo,
    tableActionEnabledCriteria,
    stationMap,
  } = props;
  const [employee, setEmployee] = useState({});
  const [updatedShapes, setUpdatedShapes] = useState(new Map());
  const [shapesColor, setShapesColor] = useState(new Map());
  const [activeTableAction, setActiveTableAction] = useState(defaultTableActionState);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [stations, setStations] = useState('');

  useEffect(() => {
    setInitialShapes();
  }, []);

  useEffect(() => {
    if (!isAllowedTableSelection()) {
      setInitialShapes();
    }
  }, [shapeProps]);

  useEffect(() => {
    const open = isAllowedTableSelection();
    setSnackbarOpen(open);
  }, [activeTableAction.assignTable, activeTableAction.mergeTable, activeTableAction.transferTable]);

  useEffect(() => {
    if (moveTableInfo && !FireboardUtils.isObjectBlank(moveTableInfo)) {
      onClickTableAction('transferTable');
    }
  }, [moveTableInfo]);

  useEffect(() => {
    setActiveTableAction(prevState => ({
      ...prevState,
      assignTable: assignTableMode,
    }));
  }, [assignTableMode]);

  const onEmployeeSelection = (emp) => {
    setEmployee(emp);
  };

  const onAssignTableClick = () => {
    let stationIds = [];
    Array.from(updatedShapes.values()).forEach(eachShape => {
      if (eachShape?.selected)
        stationIds.push(eachShape.stationId);
    });
    onAssignTable(employee, stationIds);
  };

  const setInitialShapes = () => {
    let allShapes = Array.isArray(shapeProps) ? shapeProps : [];
    let copiedShapes = new Map();
    let colors = new Map();
    allShapes.forEach(shape => {
      copiedShapes.set(shape.id, {
        ...shape,
        selected: false,
      });
      if (shape.stationId) {
        colors.set(shape.stationId, shape.stroke);
      }
    });
    setUpdatedShapes(copiedShapes);
    setShapesColor(colors);
  };

  const getStations = () => {
    let stationNumbers = '';
    if (stationMap.has(selectedShape.parentStationId)) {
      const station = stationMap.get(selectedShape.stationId);
      stationNumbers += station.number;
    } else if (selectedShape.mergedStationIds) {
      const parentStation = stationMap.get(selectedShape.stationId);
      stationNumbers += parentStation.number;
      selectedShape.mergedStationIds.forEach(stationId => {
        const station = stationMap.get(stationId);
        stationNumbers += ', ' + station.number;
      });
    }
    return stationNumbers;
  };

  const onInvokingSelectShape = (shape) => {
    if (shape?.stationId) {
      if (activeTableAction.assignTable) {
        if (shape.occupied) {
          toast.warning(strings.ASSIGN_TABLE_TEXT + ` ${shape.stationNumber} ` + strings.ASSIGN_OCCUPIED, { autoClose: 1000 });
        } else if (shape.isTableMerged) {
          toast.warning(strings.ASSIGN_TABLE_TEXT + ` ${shape.stationNumber} ` + strings.ASSIGN_ALREADY_MERGED, { autoClose: 1000 });
        } else {
          let copiedShapes = new Map(updatedShapes);
          copiedShapes.set(shape.id, {
            ...shape,
            selected: !shape.selected,
            stroke: shape.selected ? shapesColor.get(shape.stationId) : 'white',
          });
          setUpdatedShapes(copiedShapes);
        }
      } else if (activeTableAction?.transferTable) {
        if (shape.stationId === selectedShape.stationId) {
          toast.warning(strings.TRANSFER_MSG, { autoClose: 1000 });
        } else if (shape.occupied) {
          toast.warning(strings.TRANSFER_TABLE_TEXT + ` ${shape.stationNumber} ` + strings.TRANSFER_OCCUPIED, { autoClose: 1000 });
        } else {
          let copiedShapes = new Map();
          shapeProps.forEach(eachShape => {
            const isMatched = shape.stationId === eachShape.stationId;
            copiedShapes.set(eachShape.id, {
              ...eachShape,
              selected: isMatched,
              stroke: isMatched ? 'white' : eachShape.stroke
            });
          });
          setUpdatedShapes(copiedShapes);
        }
      } else if (activeTableAction?.mergeTable) {
        onTableSelection(shape);
      }
    }
  };

  const onTableClick = (selectedShape) => {
    onSelectShape && onSelectShape(selectedShape);
    clearAllTableAction();
  };

  const onTableSelection = (shape) => {
    if (shape.isTableMerged) {
      toast.warning(strings.ASSIGN_TABLE_TEXT + ` ${shape.stationNumber} ` + strings.ASSIGN_ALREADY_MERGED, { autoClose: 1000 });
    } else {
      if (shape.stationId !== selectedShape.stationId) {
        let copiedShapes = new Map(updatedShapes);
        copiedShapes.set(shape.id, {
          ...shape,
          selected: !shape.selected,
          stroke: shape.selected ? shapesColor.get(shape.stationId) : 'white',
        });
        setUpdatedShapes(copiedShapes);
      }
    }
  };

  const setUnmergeStations = () => {
    let copiedShapes = new Map();
    shapeProps.forEach(eachShape => {
      if (selectedShape.stationId === eachShape.stationId) {
        copiedShapes.set(eachShape.id, {
          ...eachShape,
          selected: true,
          stroke: selectedTableColor,
        });
      } else {
        copiedShapes.set(eachShape.id, {
          ...eachShape,
          selected: false,
          stroke: eachShape.stroke
        });
      }
    });
    const stationNumbers = getStations();
    setStations(stationNumbers);
    setUpdatedShapes(copiedShapes);
  };

  const onClickTableAction = key => {
    setActiveTableAction({
      ...defaultTableActionState,
      [key]: true,
    });
    if (key === 'unmergeTable') {
      setUnmergeStations();
    } else if (key === 'mergeTable') {
      let copiedShapes = new Map(updatedShapes);
      copiedShapes.set(selectedShape.id, {
        ...selectedShape,
        selected: true,
        stroke: selectedTableColor,
      });
      setUpdatedShapes(copiedShapes);
    }
  };

  const clearAllTableAction = () => {
    setActiveTableAction({ ...defaultTableActionState });
    setInitialShapes();
    clearMoveTableInfo && clearMoveTableInfo();
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const isAllowedTableSelection = () => {
    return activeTableAction.assignTable || activeTableAction.mergeTable || activeTableAction.transferTable;
  };

  const snackbarMessage = () => {
    let msg = ''
    if (activeTableAction.assignTable || activeTableAction.mergeTable) {
      msg = strings.ASSIGN_SNACKBAR_MSG;
    } else if (activeTableAction.transferTable) {
      msg = strings.TRANSFER_SNACKBAR_MSG;
    }
    return msg;
  };

  const getConfMsg = () => {
    let msg = '';
    if (activeTableAction.transferTable) {
      const filteredShapes = Array.from(updatedShapes.values()).filter(it => it.stationId && it.selected);
      let newStationNumber = filteredShapes.length > 0 ? filteredShapes[0].stationNumber : '';
      msg = `${moveTableInfo?.multiple ? strings.TRANSFER_MOVE_CHECKS : strings.TRANSFER_MOVE_CHECKS_FROM} ${selectedShape?.stationNumber} ${strings.TRANSFER_TO} ${newStationNumber}.`;
    }
    return msg;
  };

  const onContinue = () => {
    if (activeTableAction.transferTable) {
      const filteredShapes = Array.from(updatedShapes.values()).filter(it => it.stationId && it.selected);
      const shape = filteredShapes.length > 0 ? filteredShapes[0] : {};
      if (!shape.stationId) {
        toast.warning(strings.TRANSFER_SELECT_TO_MOVE, { autoClose: 1000 });
        return;
      }
      onMoveTable && onMoveTable(shape.stationId);
    }
    clearAllTableAction(); //reset selected table action
  };

  const goBack = () => {
    clearAllTableAction();
    goToWaitlistScreen && goToWaitlistScreen();
  };

  const onProceedWithoutTable = (proceedWithoutTableFlag) => {
    setActiveTableAction(prevState => ({
      ...prevState,
      assignTable: !proceedWithoutTableFlag,
    }));
    setInitialShapes();
  };

  const getLeftView = () => {
    if (assignTableMode) {
      return (
        <AssignTable
          onAssignTableClick={onAssignTableClick}
          goToWaitlistScreen={goToWaitlistScreen}
          selectedBooking={selectedBooking}
          onEmployeeSelection={onEmployeeSelection}
          employees={employees}
          activeTableAction={activeTableAction}
          updatedShapes={updatedShapes}
          onClickTable={onInvokingSelectShape}
          onProceedWithoutTable={onProceedWithoutTable}
        />
      )
    } else if (showTableProperties) {
      return (
        <TableProperties
          goToWaitlistScreen={goBack}
          selectedItem={selectedItem}
          selectedShape={selectedShape}
          selectedTableChecks={selectedTableChecks}
          unseatGuest={unseatGuest}
          onTableSelection={onTableSelection}
          mergeTable={onMergeTable}
          unmergeTable={invokeTableUnmerge}
          onClickSms={onClickSms}
          onVoidCheck={onVoidCheck}
          activeTableAction={activeTableAction}
          onClickTableAction={onClickTableAction}
          clearAllTableAction={clearAllTableAction}
          confirmationMessage={getConfMsg()}
          onContinue={onContinue}
          initiateMoveTable={initiateMoveTable}
          tableActionEnabledCriteria={tableActionEnabledCriteria}
          stations={stations}
          updatedShapes={updatedShapes}
        />
      )
    }
  };

  const getShapes = useCallback(() => {
    const deepCopy = new Map(updatedShapes);
    deepCopy.set(selectedShape?.id, { ...selectedShape, stroke: selectedTableColor });
    if (isAllowedTableSelection()) {
      Array.from(deepCopy.values()).forEach(it => {
        if (!it.stationId) {
          deepCopy.delete(it.id);
        }
      });
    }
    return deepCopy;
  }, [updatedShapes]);

  return (
    <Box display={'flex'} height={'100%'}>
      <Box width={'30%'} position={'relative'}>
        {getLeftView()}
        {(!!popup && popup?.type === 'confirmation') && (
          <ConfirmationPopup {...popup} />
        )}
      </Box>
      <Box width={'70%'}>
        <Whiteboard
          draggable={false}
          shapes={getShapes()}
          selectShape={isAllowedTableSelection()
            ? onInvokingSelectShape
            : assignTableMode
              ? null
              : onTableClick
          }
          backgroundColor={isAllowedTableSelection() ? '#000' : '#2a2a2a'}
          stageContainerBackgroundColor={isAllowedTableSelection() ? '#000' : '#2a2a2a'}
          tableInfoColumn='stationNumber'
          keyName1='openTime'
          keyName2_primary='closedTime'
          keyName2_secondary='totalAmount'
          keyName3='guestCount'
        />
        <Snackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          open={snackbarOpen}
          onClose={handleCloseSnackbar}
          message={snackbarMessage()}
          action={
            <Button size="small" onClick={handleCloseSnackbar}>
              Ok
            </Button>
          }
          ContentProps={{
            classes: {
              root: classes.snackbar
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default Body;
